'use client';
import { useState } from 'react';
import * as yup from 'yup';
import { useFormikContext, Formik } from 'formik';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import styles from './ForgottenPassword.module.scss';
import { useTranslation } from '@/app/i18n/client';
import { InputWithButton } from '@/components/_other/form/InputWithButton';
import { MailIcon } from '@/components/_other/icons/MailIcon';
import Alert from '@/components/_other/alert/Alert';
import TextInput from '@/components/_other/form/TextInput';
import Divider from '@/components/_other/divider/Divider';
import Button from '@/components/_other/button/Button';

const ForgottenPassword = () => {
  const { t } = useTranslation();
  const [validateStatus, setValidateStatus] = useState('');

  const formProps = {
    enableReinitialize: true,
    initialValues: {
      email: '',
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .nullable()
        .email(t('auth.forgotten_password.form.email.email_validation'))
        .required(t('auth.forgotten_password.form.email.required_validation')),
    }),
    onSubmit: async (values, { setFieldError, setErrors, resetForm }) => {
      const { email } = values;
      const reqBody = { user: { email: email } };
      try {
        setValidateStatus(null);
        const response = await fetch(`/api/lib/v1/auth/forgotten-password`, {
          method: 'POST',
          body: JSON.stringify(reqBody),
        }).then(data => data.json());

        if (response) {
          const errorMessage = get(get(response, 'messages.0'), 'message');
          if (!errorMessage) {
            resetForm();
            setValidateStatus('success');
          } else {
            setFieldError('email', errorMessage);
          }
        }
      } catch (error) {
        console.error('An unexpected error happened:', error);
        setErrors(error.data.message);
      }

      return false;
    },
  };

  return (
    <Formik {...formProps}>
      <ForgottenPasswordForm status={validateStatus} />
    </Formik>
  );
};

const ForgottenPasswordForm = ({ status }) => {
  const {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    dirty,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useFormikContext();
  const { t } = useTranslation();
  return (
    <div>
      {!isEmpty(status) ? (
          <Alert type="success" className="my-4" message={t('auth.forgotten_password.link_sent_message')} />
      ) : null}
      <form className={styles.form} onSubmit={handleSubmit}>
        <TextInput
          required
          label={'Email'}
          error={
            touched.email && errors.email ? errors.email : ''
          }
          inputProps={{
            id: 'email',
            name: 'email',
            onChange: handleChange,
            onBlur: handleBlur,
            value: values?.email,
            autoComplete: 'email',
            placeholder: t('auth.forgotten_password.form.email.placeholder'),
          }}
        />

        <Divider />

        <Button
          fullWidth={true}
          htmlType={"submit"}
          type={"primary"}
        >
          <span>
            {t('auth.forgotten_password.form.submit')}
          </span>
        </Button>
      </form>
    </div>
  );
};

export default ForgottenPassword;
